import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Product HANDLE of the auto-added "Pre Order Priority Processing" upsell line. The line
 * is excluded from the visible cart count wherever the count is shown, to match the live
 * theme and the cart drawer footer (snippets/powerlete-cart-footer.liquid), so the
 * screen-reader announcement stays in step with the visible counts. Matched by handle
 * (not title) so it still resolves in translated markets, where the title localizes.
 * @type {string}
 */
const PREORDER_HANDLE = 'pre-order-shipping';

/**
 * Total quantity of the "Pre Order Priority Processing" line in a cart payload.
 * @param {{ items?: Array<{ handle?: string, quantity?: number }> }} [cart] - A cart object (event.detail.resource).
 * @returns {number} The combined quantity of the upsell line, or 0 when it is absent.
 */
function preorderQuantity(cart) {
  const items = cart?.items;
  if (!Array.isArray(items)) return 0;

  return items.reduce(
    (total, item) => (item?.handle === PREORDER_HANDLE ? total + (item.quantity ?? 0) : total),
    0
  );
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * Scroll position captured BEFORE the body scroll-lock is applied.
   *
   * DialogComponent keeps its own #previousScrollY, but we cannot reuse it: it is private, and
   * the DialogOpenEvent we hook fires from inside showDialog()'s requestAnimationFrame, i.e.
   * AFTER `document.body.style.position = 'fixed'` has already been set. Once the body is fixed
   * the page cannot scroll, so window.scrollY reads 0 at that point (measured). Capturing here,
   * before super.showDialog() runs, is the only way to get the real value.
   * @type {number}
   */
  #scrollYBeforeOpen = 0;

  /**
   * The document's scrollRestoration setting before we forced it to 'manual', so it can be put
   * back exactly as found rather than assumed to be 'auto'.
   * @type {ScrollRestoration | null}
   */
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    // Never leave the document stuck on manual restoration if the drawer is torn down while open.
    this.#restoreScrollRestoration();
  }

  /**
   * Record the true scroll position before DialogComponent locks the body.
   * @override
   */
  showDialog() {
    if (!this.refs.dialog?.open) this.#scrollYBeforeOpen = window.scrollY;
    super.showDialog();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    // Own the scroll position for as long as the drawer is up. The history entry pushed below is
    // created while the body is locked at position:fixed, so the entry records a scroll of 0;
    // letting the browser restore that on the history.back() in #handleHistoryClose is what drops
    // the shopper at the top of the page after closing the drawer. Scoped to the drawer's
    // lifetime, so ordinary back-navigation (collection -> PDP -> back) keeps native restoration.
    if (this.#previousScrollRestoration === null && 'scrollRestoration' in history) {
      this.#previousScrollRestoration = history.scrollRestoration;
      history.scrollRestoration = 'manual';
    }

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();

    const target = this.#scrollYBeforeOpen;

    // Re-assert the position after the history traversal has actually landed.
    //
    // DialogComponent.closeDialog() already scrolls back before dispatching DialogCloseEvent, so
    // in the healthy case this is a no-op and nothing moves. It exists to correct a drift the
    // history navigation introduces: iOS Safari restores scroll on popstate far more aggressively
    // than Chrome, where this was not reproducible. Guarded by a tolerance so a correct restore is
    // never nudged, which would itself show up as a visible jump.
    //
    // Hung off `popstate` rather than a bare requestAnimationFrame: history.back() is
    // asynchronous, so a single frame can elapse BEFORE the traversal (and the browser's own
    // scroll restoration) has happened, which would make the correction a no-op at exactly the
    // moment it is needed. The nested frame lets the browser paint its restore first so ours wins.
    const correct = () => {
      if (Math.abs(window.scrollY - target) > 4) {
        window.scrollTo({ top: target, behavior: 'instant' });
      }
      this.#restoreScrollRestoration();
    };

    if (history.state?.cartDrawerOpen) {
      window.addEventListener('popstate', () => requestAnimationFrame(correct), { once: true });
      history.back();
    } else {
      // Desktop, or the entry was already consumed: no traversal is coming, so no popstate will
      // fire and the listener above would leak. Correct on the next frame instead.
      requestAnimationFrame(correct);
    }
  };

  /** Hand scrollRestoration back to the browser exactly as we found it. */
  #restoreScrollRestoration() {
    if (this.#previousScrollRestoration !== null && 'scrollRestoration' in history) {
      history.scrollRestoration = this.#previousScrollRestoration;
      this.#previousScrollRestoration = null;
    }
  }

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number, items?: Array<{ product_title?: string, quantity?: number }> } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource);
  };

  /**
   * Announces the visible cart count to screen readers when the dialog is open. The
   * auto-added "Pre Order Priority Processing" upsell line is excluded so the announced
   * count matches the header bubble and the cart drawer footer.
   * @param {{ item_count?: number, items?: Array<{ product_title?: string, quantity?: number }> } | undefined} resource - The updated cart (event.detail.resource).
   */
  #announceCartCount(resource) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || resource?.item_count === undefined) return;

    const visibleCount = Math.max(0, resource.item_count - preorderQuantity(resource));
    // Optional-chain the global Theme (matches powerlete-free-ship.js): the announce handler must
    // never throw if Theme.translations isn't defined at that moment. Fall back to the bare count
    // so the screen reader still hears the number rather than nothing.
    const countLabel = window.Theme?.translations?.cart_count;
    liveRegion.textContent = countLabel ? `${countLabel}: ${visibleCount}` : `${visibleCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
